// T1 · Rendu monnaie — total INDICATIF de la commande (Σ prix_unitaire_indicatif
// × qty) + calcul de rendu (montant reçu − total). AFFICHAGE SEUL : aucune
// écriture en base, aucun champ paiement persisté (INV-D, frontière anti-caisse).
//
// SUR MESURE (complément 7c, binding) : le prix seed n'est PAS fiable — un prix
// non arbitré n'alimente AUCUN montant affiché ni calculé. Si la commande
// contient ≥ 1 « Pizza sur mesure » : sous-total des SEULES lignes à prix
// confirmé + « prix à confirmer » + « Total : à compléter — encaissement au
// papier » ; billets et rendu MASQUÉS (pas grisés), zéro logique tarifaire
// nouvelle. Sinon : strictement inchangé.
import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { useCatalogue } from '@/hooks/useCatalogue';
import { formatEuros } from '@/lib/format';
import { NOM_SUR_MESURE } from '@/config/ordre-carte.pizznco';
import type { CommandePizza } from '@/db/schema';

interface Props {
  lignes: CommandePizza[];
  onClose: () => void;
}

const RACCOURCIS = [20, 30, 50];

export function RenduMonnaie({ lignes, onClose }: Props) {
  const catalogue = useCatalogue();
  const surMesureId = catalogue.find((p) => p.nom === NOM_SUR_MESURE)?.id;
  const nbSurMesure = lignes
    .filter((l) => l.pizzaId === surMesureId)
    .reduce((s, l) => s + l.qty, 0);
  // Sous-total des SEULES lignes à prix confirmé (sur mesure exclue).
  const total = lignes
    .filter((l) => l.pizzaId !== surMesureId)
    .reduce((s, l) => s + l.prix_unitaire_indicatif * l.qty, 0);
  const [recu, setRecu] = useState<number | null>(null);
  const rendu = recu === null ? null : recu - total;

  return (
    <div className="fixed inset-0 z-50 flex flex-col gap-6 overflow-auto bg-background p-8">
      <div className="flex items-center justify-between">
        <span className="font-cormorant text-3xl italic">Rendu monnaie</span>
        <Button
          type="button"
          variant="outline"
          onClick={onClose}
          className="min-h-[80px] min-w-[80px] text-xl"
        >
          Fermer
        </Button>
      </div>

      {nbSurMesure > 0 ? (
        // ≥ 1 sur mesure : JAMAIS le total seed (ancrage faux), aucun rendu.
        <div className="flex flex-col gap-3 font-jost text-2xl">
          <p>
            Sous-total connu :{' '}
            <span
              className="font-mono font-medium"
              data-testid="monnaie-sous-total"
            >
              {formatEuros(total)}
            </span>
          </p>
          <p data-testid="monnaie-sur-mesure">
            {nbSurMesure > 1 ? `${nbSurMesure}× ` : ''}
            {NOM_SUR_MESURE} : prix à confirmer
          </p>
          <p
            data-testid="monnaie-total-papier"
            className="font-medium text-ardoise"
          >
            Total : à compléter — encaissement au papier
          </p>
        </div>
      ) : (
        <>
          <p className="font-jost text-2xl">
            Total indicatif :{' '}
            <span
              className="font-mono font-medium"
              data-testid="monnaie-total"
            >
              {formatEuros(total)}
            </span>
          </p>

          <div className="flex flex-wrap gap-3">
            {RACCOURCIS.map((m) => (
              <Button
                key={m}
                type="button"
                variant="secondary"
                onClick={() => setRecu(m)}
                className="min-h-[80px] min-w-[80px] font-mono text-xl"
              >
                {m} €
              </Button>
            ))}
            <label className="flex flex-col gap-1">
              <span className="font-jost text-sm">Montant reçu</span>
              <Input
                inputMode="decimal"
                value={recu ?? ''}
                onChange={(e) => {
                  const v = Number.parseFloat(e.target.value.replace(',', '.'));
                  setRecu(Number.isFinite(v) ? v : null);
                }}
                placeholder="0,00"
              />
            </label>
          </div>

          {rendu !== null && (
            <p className="font-jost text-3xl">
              À rendre :{' '}
              <span
                className="font-mono font-medium"
                data-testid="monnaie-rendu"
              >
                {formatEuros(Math.max(0, rendu))}
              </span>
            </p>
          )}
        </>
      )}
    </div>
  );
}
